/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * QuestionsStore.ts - Provides access to question sets.
 */

import { AdditionCategoryName, DivisionCategoryName, isAdditionCategoryName, isDivisionCategoryName, isMultiplicationCategoryName, isSubtractionCategoryName, AggregateCategoryName, MultiplicationCategoryName, QuestionCategoryName, SubitisingCategoryName, SubtractionCategoryName, isSubitisingCategoryName, isAggregateCategoryName } from '../../categories';
import * as AdditionQuestions from '../../generated/questions/AdditionQuestions';
import * as MultiplicationQuestions from '../../generated/questions/MultiplicationQuestions';
import * as SubtractionQuestions from '../../generated/questions/SubtractionQuestions';
import * as DivisionQuestions from '../../generated/questions/DivisionQuestions';
import { unreachable } from '../../utils/typescript';
import { Format, isEqualGroupsCountersQuestion, Question, QuestionWithExtraParameters } from './types';
import { perceptual_formations_equal_groups } from '../Subitising/PercptualFormations';
import { getRandomFromArray, getRandomFromArrayWithWeights, removeRandomFromArray, Weighted } from '../../utils/collections';

/** The category names which have a pregenerated question set. */
// Right now, it is known that this is specifically the categories which aren't aggregate or subitising.
// However, this doesn't have to be the case, so this should be updated if that changes.
type PregeneratedCategoryName = Exclude<QuestionCategoryName, AggregateCategoryName | SubitisingCategoryName>;
function isPregeneratedCategoryName(category: QuestionCategoryName): category is PregeneratedCategoryName {
  return !isAggregateCategoryName(category) && !isSubitisingCategoryName(category);
}

/** A question store - can provide questions on request. Internally manages how those questions are obtained. */
export interface QuestionsStore {
  readonly category: QuestionCategoryName;
  getQuestion(): QuestionWithExtraParameters;
  reset(): void
}

export function getStoreForCategory(category: QuestionCategoryName): QuestionsStore {
  if (isSubitisingCategoryName(category)) {
    throw new Error('Subitizing questions are not supported');
  }

  if (isPregeneratedCategoryName(category)) {
    return new PregeneratedQuestionsStore(category);
  } else if (isAggregateCategoryName(category)) {
    return new AggregateQuestionsStore(category);
  } else {
    unreachable(category);
  }
}

export class PregeneratedQuestionsStore implements QuestionsStore {
  readonly category: PregeneratedCategoryName;
  readonly pregeneratedQuestions: readonly Question[];
  availableQuestions: number[] = [];

  constructor(category: PregeneratedCategoryName) {
    this.category = category;
    this.pregeneratedQuestions = categoryToPregeneratedQuestions(category);
  }

  reset(): void {
    this.availableQuestions = [];
  }

  getQuestion(): QuestionWithExtraParameters {
    if (this.availableQuestions.length === 0) {
      this.availableQuestions = this.pregeneratedQuestions.map((_question, index) => index);
    }

    const index = removeRandomFromArray(this.availableQuestions);
    if (index === null) { throw new Error('No pregenerated questions found'); }
    const question = this.pregeneratedQuestions[index];

    if (isEqualGroupsCountersQuestion(question)) {
      // Special case code for equal group counters - need some extra random info set.
      // This is one situation where it's OK to use !, as we know that the array
      // passed into `getRandomFromArray` is not empty, so the return value cannot be null.
      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      switch (question.format) {
        case Format.EqualGroupsCountersAB:
          return {
            ...question,
            formationA: getRandomFromArray(perceptual_formations_equal_groups[question.numA])!,
            formationB: getRandomFromArray(perceptual_formations_equal_groups[question.numB])!,
          };
        case Format.EqualGroupsCountersABC:
          return {
            ...question,
            formationA: getRandomFromArray(perceptual_formations_equal_groups[question.numA])!,
            formationB: getRandomFromArray(perceptual_formations_equal_groups[question.numB])!,
            formationC: getRandomFromArray(perceptual_formations_equal_groups[question.numC])!,
          };
        case Format.EqualGroupsCountersABCD:
          return {
            ...question,
            formationA: getRandomFromArray(perceptual_formations_equal_groups[question.numA])!,
            formationB: getRandomFromArray(perceptual_formations_equal_groups[question.numB])!,
            formationC: getRandomFromArray(perceptual_formations_equal_groups[question.numC])!,
            formationD: getRandomFromArray(perceptual_formations_equal_groups[question.numD])!,
          };
      }
      /* eslint-enable @typescript-eslint/no-non-null-assertion */
    } else {
      return question;
    }
  }
}

export class AggregateQuestionsStore implements QuestionsStore {
  readonly category: AggregateCategoryName;
  questionStores: Weighted<QuestionsStore>[];

  constructor(category: AggregateCategoryName) {
    this.category = category;
    const subCategories = aggregateCategoryToSubCategories(category);
    this.questionStores = subCategories.map(category => {
      if (typeof category === 'string') {
        // Default to weight of 1.
        return { item: getStoreForCategory(category), weight: 1 };
      } else {
        const { item , weight } = category;
        return { item: getStoreForCategory(item), weight };
      }
    });
  }

  reset(): void {
    this.questionStores.forEach(store => store.item.reset);
  }

  getQuestion(): QuestionWithExtraParameters {
    const store = getRandomFromArrayWithWeights(this.questionStores);
    if (store === null) { throw new Error('No sub-categories found.'); }
    return store.getQuestion();
  }
}

/** 
 * Get array of pregenerated questions from category name.
 */
function categoryToPregeneratedQuestions(category: PregeneratedCategoryName): readonly Question[] {
  if (isAdditionCategoryName(category)) {
    switch (category) {
      case AdditionCategoryName.ADDING_0_1_2: { return AdditionQuestions.add0or1or2Questions; }
      case AdditionCategoryName.BONDS_TO_10: { return AdditionQuestions.bondsTo10Questions; }
      case AdditionCategoryName.BONDS_TO_20: { return AdditionQuestions.bondsTo20Questions; }
      case AdditionCategoryName.BONDS_TO_100: { return AdditionQuestions.bondsTo100TensQuestions; }
      case AdditionCategoryName.BONDS_TO_100_ONES: { return AdditionQuestions.bondsTo100OnesQuestions; }
      case AdditionCategoryName.ADDING_10: { return AdditionQuestions.adding10Questions; }
      case AdditionCategoryName.DOUBLES: { return AdditionQuestions.doublesQuestions; }
      case AdditionCategoryName.NEAR_DOUBLES: { return AdditionQuestions.nearDoublesQuestions; }
      case AdditionCategoryName.ADD_ACROSS_10: { return AdditionQuestions.addAcross10Questions; }
      case AdditionCategoryName.ADD_ONES_WITHIN_20: { return AdditionQuestions.addOnesWithin20Questions; }
      case AdditionCategoryName.ADD_ONES_WITHIN_100: { return AdditionQuestions.addOnesWithin100Questions; }
      case AdditionCategoryName.ADD_WITHIN_10: { return AdditionQuestions.addWithin10Questions; }
      case AdditionCategoryName.ADD_TWO_TENS_WITHIN_100: { return AdditionQuestions.addTwoTensWithin100Questions; }
      case AdditionCategoryName.ADD_TENS_WITHIN_100: { return AdditionQuestions.addTensWithin100Questions; }
    }
  } else if (isMultiplicationCategoryName(category)) {
    switch (category) {
      case MultiplicationCategoryName.SEQUENCE_2: { return MultiplicationQuestions.sequence2Questions; }
      case MultiplicationCategoryName.SEQUENCE_5: { return MultiplicationQuestions.sequence5Questions; }
      case MultiplicationCategoryName.SEQUENCE_10: { return MultiplicationQuestions.sequence10Questions; }
      case MultiplicationCategoryName.SEQUENCE_3: { return MultiplicationQuestions.sequence3Questions; }
      case MultiplicationCategoryName.SEQUENCE_100: { return MultiplicationQuestions.sequence100Questions; }
      case MultiplicationCategoryName.MULTIPLY_TWO: { return MultiplicationQuestions.multiply2Questions; }
      case MultiplicationCategoryName.MULTIPLY_THREE: { return MultiplicationQuestions.multiply3Questions; }
      case MultiplicationCategoryName.MULTIPLY_FOUR: { return MultiplicationQuestions.multiply4Questions; }
      case MultiplicationCategoryName.MULTIPLY_FIVE: { return MultiplicationQuestions.multiply5Questions; }
      case MultiplicationCategoryName.MULTIPLY_SIX: { return MultiplicationQuestions.multiply6Questions; }
      case MultiplicationCategoryName.MULTIPLY_SEVEN: { return MultiplicationQuestions.multiply7Questions; }
      case MultiplicationCategoryName.MULTIPLY_EIGHT: { return MultiplicationQuestions.multiply8Questions; }
      case MultiplicationCategoryName.MULTIPLY_NINE: { return MultiplicationQuestions.multiply9Questions; }
      case MultiplicationCategoryName.MULTIPLY_TEN: { return MultiplicationQuestions.multiply10Questions; }
      case MultiplicationCategoryName.MULTIPLY_ELEVEN: { return MultiplicationQuestions.multiply11Questions; }
      case MultiplicationCategoryName.MULTIPLY_TWELVE: { return MultiplicationQuestions.multiply12Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_TWO: { return MultiplicationQuestions.timesTableFacts2Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_THREE: { return MultiplicationQuestions.timesTableFacts3Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_FOUR: { return MultiplicationQuestions.timesTableFacts4Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_FIVE: { return MultiplicationQuestions.timesTableFacts5Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_SIX: { return MultiplicationQuestions.timesTableFacts6Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_SEVEN: { return MultiplicationQuestions.timesTableFacts7Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_EIGHT: { return MultiplicationQuestions.timesTableFacts8Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_NINE: { return MultiplicationQuestions.timesTableFacts9Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_TEN: { return MultiplicationQuestions.timesTableFacts10Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_ELEVEN: { return MultiplicationQuestions.timesTableFacts11Questions; }
      case MultiplicationCategoryName.TIMES_TABLE_FACTS_TWELVE: { return MultiplicationQuestions.timesTableFacts12Questions; }
      case MultiplicationCategoryName.EQUAL_GROUPS_OBJECTS: { return MultiplicationQuestions.equalGroupsObjectsQuestions; }
      case MultiplicationCategoryName.EQUAL_GROUPS_COUNTERS: { return MultiplicationQuestions.equalGroupsCountersQuestions; }
    }
  } else if (isSubtractionCategoryName(category)) {
    switch (category) {
      case SubtractionCategoryName.SUBTRACT_1: { return SubtractionQuestions.subtract1Questions; }
      case SubtractionCategoryName.SUBTRACT_2: { return SubtractionQuestions.subtract2Questions; }
      case SubtractionCategoryName.DIFFERENCE: { return SubtractionQuestions.differenceQuestions; }
      case SubtractionCategoryName.SUBTRACT_FROM_10: { return SubtractionQuestions.subtractFrom10Questions; }
      case SubtractionCategoryName.SUBTRACT_WITHIN_10: { return SubtractionQuestions.subtractWithin10Questions; }
      case SubtractionCategoryName.SUBTRACT_ACROSS_10: { return SubtractionQuestions.subtractAcross10Within20Questions; }
      case SubtractionCategoryName.SUBTRACT_ACROSS_TENS: { return SubtractionQuestions.subtractAcrossTensWithin100Questions; }
      case SubtractionCategoryName.SUBTRACT_ONES: { return SubtractionQuestions.subtractOnesWithin100Questions; }
      case SubtractionCategoryName.SUBTRACT_10: { return SubtractionQuestions.subtract10Questions; }
      case SubtractionCategoryName.SUBTRACT_TENS: { return SubtractionQuestions.subtractTensQuestions; }
    }
  } else if (isDivisionCategoryName(category)) {
    switch (category) {
      case DivisionCategoryName.HALVES: { return DivisionQuestions.halvesQuestions; }
      case DivisionCategoryName.DIVIDE_2: { return DivisionQuestions.divide2Questions; }
      case DivisionCategoryName.DIVIDE_10: { return DivisionQuestions.divide10Questions; }
      case DivisionCategoryName.DIVIDE_5: { return DivisionQuestions.divide5Questions; }
      case DivisionCategoryName.TWO_TIMES_TABLE: { return DivisionQuestions.twoTimesTableQuestions; }
      case DivisionCategoryName.THREE_TIMES_TABLE: { return DivisionQuestions.threeTimesTableQuestions; }
      case DivisionCategoryName.FOUR_TIMES_TABLE: { return DivisionQuestions.fourTimesTableQuestions; }
      case DivisionCategoryName.FIVE_TIMES_TABLE: { return DivisionQuestions.fiveTimesTableQuestions; }
      case DivisionCategoryName.SIX_TIMES_TABLE: { return DivisionQuestions.sixTimesTableQuestions; }
      case DivisionCategoryName.SEVEN_TIMES_TABLE: { return DivisionQuestions.sevenTimesTableQuestions; }
      case DivisionCategoryName.EIGHT_TIMES_TABLE: { return DivisionQuestions.eightTimesTableQuestions; }
      case DivisionCategoryName.NINE_TIMES_TABLE: { return DivisionQuestions.nineTimesTableQuestions; }
      case DivisionCategoryName.TEN_TIMES_TABLE: { return DivisionQuestions.tenTimesTableQuestions; }
      case DivisionCategoryName.ELEVEN_TIMES_TABLE: { return DivisionQuestions.elevenTimesTableQuestions; }
      case DivisionCategoryName.TWELVE_TIMES_TABLE: { return DivisionQuestions.twelveTimesTableQuestions; }
      case DivisionCategoryName.ODD_EVEN: { return DivisionQuestions.oddEvenQuestions; }
    }
  } else {
    unreachable(category);
  }
}

/** If the weight is not given, we assume a weight of 1. */
type QuestionCategoryNameWeighted = QuestionCategoryName | Weighted<QuestionCategoryName>;

/** 
 * Get array of sub-categories from an aggregate category.
 * Never returns an empty array.
 */
function aggregateCategoryToSubCategories(category: AggregateCategoryName): QuestionCategoryNameWeighted[] {
  if (isSubitisingCategoryName(category)) {
    throw new Error('Subitizing questions are not supported');
  }

  if (isAdditionCategoryName(category)) {
    const magicMix10Questions = [
      AdditionCategoryName.ADDING_0_1_2,
      AdditionCategoryName.BONDS_TO_10,
      AdditionCategoryName.ADD_WITHIN_10,
      AdditionCategoryName.DOUBLES,
      AdditionCategoryName.NEAR_DOUBLES,
    ];
    const magicMix20Questions = [
      ...magicMix10Questions,
      AdditionCategoryName.BONDS_TO_20,
      AdditionCategoryName.ADD_ONES_WITHIN_20,
      AdditionCategoryName.ADDING_10,
      AdditionCategoryName.ADD_ACROSS_10,
    ];
    const magicMix100Questions = [
      ...magicMix20Questions,
      AdditionCategoryName.ADD_TENS_WITHIN_100,
      AdditionCategoryName.ADD_TWO_TENS_WITHIN_100,
      AdditionCategoryName.BONDS_TO_100,
      AdditionCategoryName.BONDS_TO_100_ONES,
      AdditionCategoryName.ADD_ONES_WITHIN_100,
    ];
  
    switch (category) {
      case AdditionCategoryName.MAGIC_MIX_10_ADDITION: { return magicMix10Questions; }
      case AdditionCategoryName.MAGIC_MIX_20_ADDITION: { return magicMix20Questions; }
      case AdditionCategoryName.MAGIC_MIX_100_ADDITION: { return magicMix100Questions; }
    }

  } else if (isMultiplicationCategoryName(category)) {
    const magicMixQuestions: QuestionCategoryNameWeighted[] = [
      MultiplicationCategoryName.SEQUENCE_2,
      MultiplicationCategoryName.SEQUENCE_3,
      MultiplicationCategoryName.SEQUENCE_5,
      MultiplicationCategoryName.SEQUENCE_10,
      MultiplicationCategoryName.SEQUENCE_100,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_TWO,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_THREE,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_FOUR,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_FIVE,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_SIX,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_SEVEN,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_EIGHT,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_NINE,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_TEN,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_ELEVEN,
      MultiplicationCategoryName.TIMES_TABLE_FACTS_TWELVE,
      { item: MultiplicationCategoryName.EQUAL_GROUPS_OBJECTS, weight: 6 },
      { item: MultiplicationCategoryName.EQUAL_GROUPS_COUNTERS, weight: 6 },
      MultiplicationCategoryName.MULTIPLY_TWO,
      MultiplicationCategoryName.MULTIPLY_THREE,
      MultiplicationCategoryName.MULTIPLY_FOUR,
      MultiplicationCategoryName.MULTIPLY_FIVE,
      MultiplicationCategoryName.MULTIPLY_SIX,
      MultiplicationCategoryName.MULTIPLY_SEVEN,
      MultiplicationCategoryName.MULTIPLY_EIGHT,
      MultiplicationCategoryName.MULTIPLY_NINE,
      MultiplicationCategoryName.MULTIPLY_TEN,
      MultiplicationCategoryName.MULTIPLY_ELEVEN,
      MultiplicationCategoryName.MULTIPLY_TWELVE,
    ];

    switch (category) {
      case MultiplicationCategoryName.MAGIC_MIX_MULTIPLICATION: { return magicMixQuestions; }
    }

  } else if (isSubtractionCategoryName(category)) {
    const magicMix10Questions = [
      SubtractionCategoryName.SUBTRACT_1,
      SubtractionCategoryName.SUBTRACT_2,
      SubtractionCategoryName.SUBTRACT_FROM_10,
      SubtractionCategoryName.SUBTRACT_WITHIN_10,
    ];
    const magicMix20Questions = [
      ...magicMix10Questions,
      SubtractionCategoryName.DIFFERENCE,
      SubtractionCategoryName.SUBTRACT_10,
      SubtractionCategoryName.SUBTRACT_ACROSS_10,
    ];
    const magicMix100Questions = [
      ...magicMix20Questions,
      SubtractionCategoryName.SUBTRACT_TENS,
      SubtractionCategoryName.SUBTRACT_ACROSS_TENS,
      SubtractionCategoryName.SUBTRACT_ONES,
    ];

    switch (category) {
      case SubtractionCategoryName.MAGIC_MIX_10_SUBTRACTION: { return magicMix10Questions; }
      case SubtractionCategoryName.MAGIC_MIX_20_SUBTRACTION: { return magicMix20Questions; }
      case SubtractionCategoryName.MAGIC_MIX_100_SUBTRACTION: { return magicMix100Questions; }
    }

  } else if (isDivisionCategoryName(category)) {
    const magicMixQuestions: QuestionCategoryNameWeighted[] = [
      DivisionCategoryName.HALVES,
      DivisionCategoryName.DIVIDE_2,
      DivisionCategoryName.DIVIDE_5,
      DivisionCategoryName.DIVIDE_10,
      DivisionCategoryName.TWO_TIMES_TABLE,
      DivisionCategoryName.THREE_TIMES_TABLE,
      DivisionCategoryName.FOUR_TIMES_TABLE,
      DivisionCategoryName.FIVE_TIMES_TABLE,
      DivisionCategoryName.SIX_TIMES_TABLE,
      DivisionCategoryName.SEVEN_TIMES_TABLE,
      DivisionCategoryName.EIGHT_TIMES_TABLE,
      DivisionCategoryName.NINE_TIMES_TABLE,
      DivisionCategoryName.TEN_TIMES_TABLE,
      DivisionCategoryName.ELEVEN_TIMES_TABLE,
      DivisionCategoryName.TWELVE_TIMES_TABLE,
      { item: DivisionCategoryName.ODD_EVEN, weight: 6 },
    ];

    switch (category) {
      case DivisionCategoryName.MAGIC_MIX_DIVISION: { return magicMixQuestions; }
    }

  } else {
    unreachable(category);
  }
}
